import React, { FC, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { Button } from '@mui/material';
import { styled } from '@mui/material';
import cx from 'classnames';
import { useInfiniteHits } from 'react-instantsearch';

import { useDiscoverContext } from '~/context/DiscoverContext';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import { GET_LIST_COURSES_LOGGED_IN } from '~/graphql/courses';
import { GET_DISCOVER_PATHWAYS_DETAILED_INFO } from '~/graphql/pathways';
import { GET_VIDEO_PROGRESS_LIST } from '~/graphql/videos';
import useSERPNavigation from '~/hooks/useSERPNavigation';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import useWatchList from '~/hooks/useWatchList';
import GlossaryInfiniteHits from '~/pages/DiscoverPage/components/GlossaryInfiniteHits/GlossaryInfiniteHits';
import GridHit from '~/pages/DiscoverPage/components/GridHit/GridHit';
import Hit from '~/pages/DiscoverPage/components/Hit/Hit';
import LoadingHits from '~/pages/DiscoverPage/components/LoadingHits';
import type { AggregatorHit } from '~/pages/DiscoverPage/const';
import { sendAlgoliaClickEvent } from '~/pages/DiscoverPage/helpers';
import { HITS_TYPES } from '~/pages/const';
import {
  LARGE_MARGIN_PX,
  MIDDLE_MARGIN_PX,
  SMALL_MARGIN,
  SMALL_MARGIN_PX,
  useIsWidthUp,
} from '~/theme';
import {
  Query,
  QueryGet_Discover_Pathways_Detailed_InfoArgs,
  QueryListCoursesLogedInArgs,
  QueryListVideoProgressArgs,
} from '~/types';

interface InfiniteHitsProps {
  page: number | null;
  limit: number;
  view: string;
  total: number | null;
  linkedInId?: string | null;
  loading: boolean;
}

export const InfiniteHits: FC<InfiniteHitsProps> = ({ page, limit, total, view, loading }) => {
  const { t } = useTenantTranslation();
  const isDesktop = useIsWidthUp('md');

  const isGridView = view === 'grid' && isDesktop;
  const showedItems = page ? (page + 1) * limit : limit;

  const serpType = useSERPNavigation();
  const onGlossaryPage = serpType === HITS_TYPES.GLOSSARY;
  const onVideoPage = serpType === HITS_TYPES.VIDEO;

  const { searchState } = useDiscoverContext();
  const { userInfo } = useUserInfoContext();
  const { hits, isLastPage, showMore, sendEvent } = useInfiniteHits<AggregatorHit>();

  const videoIds = useMemo(
    () =>
      hits
        ?.filter((item) => item?.searchable_type?.toString() === HITS_TYPES.VIDEO)
        .map((item) => item?.id?.toString()) || [],
    [hits],
  );
  const pathwayIds = useMemo(
    () =>
      hits
        .filter((item) => item?.searchable_type?.toString() === HITS_TYPES.PATHWAY)
        .map((item) => item.id) || [],
    [hits],
  );
  const courseIds = useMemo(
    () =>
      hits
        .filter((item) => item?.searchable_type?.toString() === HITS_TYPES.COURSE)
        .map((item) => item.id.toString()) || [],
    [hits],
  );

  const { data: pathwaysProgressData } = useQuery<
    Pick<Query, 'get_discover_pathways_detailed_info'>,
    QueryGet_Discover_Pathways_Detailed_InfoArgs
  >(GET_DISCOVER_PATHWAYS_DETAILED_INFO, {
    variables: {
      pathway_ids: pathwayIds,
    },
    skip: pathwayIds.length === 0 || !userInfo,
    fetchPolicy: 'network-only',
  });

  const pathwaysProgress = pathwaysProgressData?.get_discover_pathways_detailed_info || [];

  const { data: coursesData } = useQuery<
    Pick<Query, 'listCoursesLogedIn'>,
    QueryListCoursesLogedInArgs
  >(GET_LIST_COURSES_LOGGED_IN, {
    variables: { courseIds: courseIds },
    skip: courseIds.length === 0 || !userInfo,
    fetchPolicy: 'network-only',
  });

  const { data: videoProgressData } = useQuery<
    Pick<Query, 'listVideoProgress'>,
    QueryListVideoProgressArgs
  >(GET_VIDEO_PROGRESS_LIST, {
    variables: {
      filter: {
        user_id: userInfo?.id || '',
        video_ids: videoIds,
      },
    },
    skip: !userInfo?.id || videoIds.length === 0 || isGridView,
    fetchPolicy: 'network-only',
  });

  const videosProgress = videoProgressData?.listVideoProgress || [];

  //fill video ids for all pages except non-video search type pages (pathways, insights, etc.)
  const videosIds =
    onVideoPage || !serpType
      ? hits
          ?.filter((entity) => entity?.searchable_type?.toString() === HITS_TYPES.VIDEO)
          ?.map((entity) => entity?.id)
          ?.filter((item): item is number => Boolean(item))
          ?.map((item) => item.toString())
      : null;

  const watchlistById = useWatchList(videosIds);

  return (
    <Wrapper>
      <ul
        className={cx(
          isGridView ? 'ais-InfiniteHits-grid' : 'ais-InfiniteHits-list',
          onGlossaryPage && 'ais-GlossaryInfiniteHits-list',
        )}
      >
        {loading && (
          <LoadingHits
            isGridView={isGridView}
            serpType={serpType}
            hasSearchTerm={searchState?.query}
          />
        )}
        {loading ? null : onGlossaryPage && !searchState?.query ? (
          <GlossaryInfiniteHits hits={hits} isGridView={isGridView} sendEvent={sendEvent} />
        ) : (
          hits.map((hit) => {
            const searchableType = hit?.searchable_type?.toString();
            const pathwayProgress = pathwaysProgress.find(
              (item) => item?.id === hit?.id && searchableType === HITS_TYPES.PATHWAY,
            );

            const videoProgress = videosProgress.find(
              (item) =>
                item?.video?.id === hit?.id.toString() && searchableType === HITS_TYPES.VIDEO,
            );
            const handleClickHit = () => sendAlgoliaClickEvent(sendEvent, hit);

            return (
              <li data-testid="resultItem" key={hit.objectID}>
                {isGridView ? (
                  <GridHit pathwayProgress={pathwayProgress} hit={hit} onClick={handleClickHit} />
                ) : (
                  <Hit
                    hit={hit}
                    pathwayProgress={pathwayProgress}
                    coursesByIdMap={coursesData?.listCoursesLogedIn?.reduce(
                      (acc, course) => ({
                        ...acc,
                        [course?.id || '']: course,
                      }),
                      {},
                    )}
                    videoProgress={videoProgress}
                    isInWatchlist={watchlistById[hit.id]}
                    onClick={handleClickHit}
                  />
                )}
              </li>
            );
          })
        )}
      </ul>
      {!isLastPage && (
        <ViewBtn onClick={showMore}>
          {t('button.viewMore')} - {showedItems} {t('button.of')} {total}
        </ViewBtn>
      )}
    </Wrapper>
  );
};

export default InfiniteHits;

const Wrapper = styled('div')`
  & .ais-InfiniteHits-list {
    list-style-type: none;
    padding-left: 0;

    ${({ theme }) => theme.breakpoints.up('md')} {
      & > li {
        margin-bottom: 32px;
        padding-bottom: 32px;
        border-bottom: 1px solid ${({ theme }) => theme.palette.common.category.cpd};

        &:last-child {
          border-bottom: none;
          margin-bottom: 0;
        }
      }
    }
  }

  & .ais-InfiniteHits-list.ais-GlossaryInfiniteHits-list {
    & > li {
      margin-bottom: ${MIDDLE_MARGIN_PX};
      padding-bottom: 0;
      border-bottom: none;

      ${({ theme }) => theme.breakpoints.up('md')} {
        margin-bottom: ${LARGE_MARGIN_PX};
      }

      &:last-child {
        margin-bottom: ${MIDDLE_MARGIN_PX};

        ${({ theme }) => theme.breakpoints.up('md')} {
          margin-bottom: ${LARGE_MARGIN_PX};
        }
      }

      &.hit-letter {
        padding-bottom: ${SMALL_MARGIN / 2}px;
        border-bottom: 3px solid ${({ theme }) => theme.palette.common.violetAnalyticsCard};
      }
    }
  }

  & .ais-InfiniteHits-grid {
    list-style-type: none;
    padding-left: 0;
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    margin: 0;
    margin-right: -${SMALL_MARGIN_PX};

    & > li {
      margin-right: ${SMALL_MARGIN_PX};
      width: calc((100% - ${3 * SMALL_MARGIN}px) / 3);
    }

    &.ais-GlossaryInfiniteHits-list {
      & > li {
        width: 100%;
      }
    }
  }
`;
const ViewBtn = styled(Button)`
  width: 100%;
  text-transform: none;
  text-align: center;
  font-size: 18px;
  line-height: 1;
  padding: ${SMALL_MARGIN_PX};
  color: ${({ theme }) => theme.palette.common.text.secondaryToPrimary};
  background: ${({ theme }) => theme.palette.common.category.cpd};
  border-radius: 5px;
  font-weight: bold;
  @media (hover: none) {
    &:hover {
      background-color: ${({ theme }) => theme.palette.common.category.cpd};
    }
  }
`;
